<template>
  <data-table-row
    :clickable="!row.pendingAdd"
    :class="{
      'ai-visibility-question-table-row--pending-add': row.pendingAdd,
      'ai-visibility-question-table-row--pending-remove': row.pendingRemove,
    }"
    @click="$emit('select-question', row)"
  >
    <data-table-cell width="490px">
      <div class="ai-visibility-question-table-row__question">
        <span class="ai-visibility-question-table-row__number">{{ rowNumber }}.</span>
        <div>
          <strong>
            {{ row.questionText }}
            <span
              v-if="row.pendingAdd"
              class="ai-visibility-question-table-row__new"
            >
              NEW
            </span>
          </strong>
          <p>
            <span class="ai-visibility-question-table-row__note">{{ row.tableMetaLabel }}</span>
          </p>
        </div>
      </div>
    </data-table-cell>
    <data-table-cell width="240px" align="center" class="ai-visibility-question-table-row__status">
      <h2 class="ai-visibility-question-table-row__label"
          :class="`ai-visibility-question-table-row__label-${row.statusTone}`">{{ row.statusLabel }}</h2>
      <p v-if="row.rankSupportLabel" class="ai-visibility-question-table-row__note">{{ row.rankSupportLabel }}</p>
    </data-table-cell>
    <data-table-cell width="240px" align="center" class="ai-visibility-question-table-row__visibility-score">
      <span v-if="row.pendingAdd" class="ai-visibility-question-table-row__empty-metric">-</span>
      <div v-else class="ai-visibility-question-table-row__visibility-score-bar">
        <span
            class="ai-visibility-question-table-row__label"
            :class="`ai-visibility-question-table-row__label-${visibilityScoreTone}`"
        >
          {{ row.yourVisibilityScore.current }}
        </span>
        <span class="ai-visibility-question-table-row__note">/100</span>
        <progress-scale
            :value="row.yourVisibilityScore.current"
            :tone="visibilityScoreTone"
        />
      </div>

      <trend-badge
          v-if="visibilityTrend"
          :direction="visibilityTrend.direction"
          :tone="visibilityTrend.tone"
          :label="visibilityTrend.label"
      />
    </data-table-cell>
    <data-table-cell width="160px" align="center">
      <strong>{{ row.rankLabel }}</strong>
      <p v-if="row.rankDenominatorLabel" class="ai-visibility-question-table-row__note">{{ row.rankDenominatorLabel }}</p>
      <trend-badge
          v-if="showTrend"
          :direction="row.trend.direction"
          :tone="row.trend.tone"
          :label="row.trend.label"
      />
    </data-table-cell>
    <data-table-cell width="90px" align="center">
      <button
        v-if="row.pendingAdd"
        type="button"
        class="ai-visibility-question-table-row__action"
        title="Remove staged question"
        aria-label="Remove staged question"
        @click.stop="$emit('remove-pending-question', row)"
      >
        <i class="material-icons-outlined">close</i>
      </button>
      <button
        v-else-if="row.pendingRemove"
        type="button"
        class="ai-visibility-question-table-row__undo"
        @click.stop="$emit('undo-remove-question', row)"
      >
        Undo
      </button>
      <button
        v-else
        type="button"
        class="ai-visibility-question-table-row__action"
        title="Delete question"
        aria-label="Delete question"
        @click.stop="$emit('stage-remove-question', row)"
      >
        <i class="mdi mdi-trash-can-outline"></i>
      </button>
    </data-table-cell>
  </data-table-row>
</template>

<script>
import DataTableRow from '../../../ui/table/DataTableRow.vue'
import DataTableCell from '../../../ui/table/DataTableCell.vue'
import TrendBadge from '../../../ui/display/TrendBadge.vue'
import ProgressScale from "../../../ui/display/ProgressScale.vue";

export default {
  name: 'QuestionTableRow',
  components: {
    ProgressScale,
    DataTableRow,
    DataTableCell,
    TrendBadge,
  },
  props: {
    row: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    indexOffset: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    rowNumber() {
      return this.indexOffset + this.index + 1
    },
    showTrend() {
      return this.row.status !== 'unknown' && this.row.status !== 'missing' && Boolean(this.row.trend?.value)
    },
    visibilityScoreTone() {
      if (this.row.yourVisibilityScore.current > 60) {
        return 'success'
      } else if (this.row.yourVisibilityScore.current > 30) {
        return 'warning'
      } else {
        return 'danger'
      }
    },
    visibilityTrend() {
      if (this.row.yourVisibilityScore.change === null || this.row.yourVisibilityScore.change === 0) {
        return null
      }
      if (this.row.yourVisibilityScore.change > 0) {
        return {
          direction: 'up',
          tone: "positive",
          label: `+${this.row.yourVisibilityScore.change} vs. last week`
        }
      }
      if (this.row.yourVisibilityScore.change < 0) {
        return {
          direction: 'down',
          tone: "negative",
          label: `${this.row.yourVisibilityScore.change} vs. last week`
        }
      }
      return null
    }
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-question-table-row__visibility-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.ai-visibility-question-table-row__visibility-score-bar {
  width: 70%;
}

.ai-visibility-question-table-row__question {
  display: flex;
  gap: 10px;
  min-width: 0;
}

.ai-visibility-question-table-row__question > div {
  min-width: 0;
}

.ai-visibility-question-table-row__question strong,
.ai-visibility-question-table-row__question p,
.ai-visibility-question-table-row__note {
  overflow-wrap: anywhere;
}

.ai-visibility-question-table-row__question strong {
  display: block;
  color: var(--v-mainColor-base);
  line-height: 1.35;
  font-size: 16px;
  font-weight: 400;
}

.ai-visibility-question-table-row__question p,
.ai-visibility-question-table-row__note {
  color: var(--v-gray36Color-base);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  margin: 4px 0 0;
}

.ai-visibility-question-table-row__question p {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.ai-visibility-question-table-row__number {
  flex: 0 0 auto;
  color: var(--v-mainColor-base);
}

.ai-visibility-question-table-row__status {
  display: flex;
  flex-direction: column;
  gap: 6px;

}

.ai-visibility-question-table-row__label {
  font-size: 16px;
  font-weight: 700;
}

.ai-visibility-question-table-row__label-danger {
  color: #FF4040;
}

.ai-visibility-question-table-row__label-success {
  color: #11A33D;
}

.ai-visibility-question-table-row__label-warning {
  color: #F97316;
}

.ai-visibility-question-table-row__label-info {
  color: #316BFF;
}

.ai-visibility-question-table-row__label-neutral {
  color: var(--v-gray36Color-base);
}

.ai-visibility-question-table-row__new {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: var(--v-profileAnchorColor-base);
  color: var(--v-whiteColor-base);
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  margin-left: 6px;
  padding: 2px 8px;
  vertical-align: middle;
}

.ai-visibility-question-table-row--pending-add {
  background: rgba(255, 87, 0, 0.05);
}

.ai-visibility-question-table-row--pending-remove {
  opacity: 0.62;
}

.ai-visibility-question-table-row--pending-remove .ai-visibility-question-table-row__question strong {
  text-decoration: line-through;
}

.ai-visibility-question-table-row__action,
.ai-visibility-question-table-row__undo {
  display: inline-flex;
  min-width: 34px;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 18px;
  background: var(--v-cardBg-base);
  color: var(--v-mainColor-base);
  cursor: pointer;
  padding: 6px 10px;
}

.ai-visibility-question-table-row__action i {
  font-size: 18px;
}

.ai-visibility-question-table-row__undo {
  color: var(--v-profileAnchorColor-base);
  font-size: 13px;
  font-weight: 700;
}

.ai-visibility-question-table-row__empty-metric {
  color: var(--v-gray36Color-base);
  font-size: 18px;
}
</style>
